<script setup lang="ts">
// NPM
import { nextTick, onBeforeMount, ref } from 'vue';
import { useMq } from 'vue3-mq';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

// Components
import { BaseCard, BaseEmptyState } from '@magma-app/magma-lapilli';
import BaseRewardDetails from '@/components/bases/BaseRewardDetails.vue';

// Stores
import { useCommonStore } from '@/stores/common';
import { useRewardsStore } from '@/stores/rewards';
import { useUserStore } from '@/stores/user';
import type { UserDashboardGetRewardsResponseType } from '@magma-app/magma-types/all';
const commonStore = useCommonStore();
const rewardsStore = useRewardsStore();
const userStore = useUserStore();

// Plugins
const mq = useMq();
const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });

// Reactive variables
const initialized = ref(false);
const rewardDetails = ref<UserDashboardGetRewardsResponseType[number] | null>(
  null,
);

// Lifecycle hooks
onBeforeMount(async () => {
  try {
    await rewardsStore.getRewards();
  } catch {
    // Keep the target in the URL so a transient request failure can be retried.
    initialized.value = true;
    return;
  }

  initialized.value = true;

  const rawRewardId = Array.isArray(route.query.rewardId)
    ? route.query.rewardId[0]
    : route.query.rewardId;
  const rewardId = Number(rawRewardId);
  if (!('rewardId' in route.query)) return;

  const query = { ...route.query };
  delete query.rewardId;

  if (!Number.isInteger(rewardId) || rewardId <= 0) {
    commonStore.setAlert({
      open: true,
      type: 'error',
      title: t('actionDeepLink.notFound.title'),
      description: t('actionDeepLink.notFound.reward'),
      duration: 5000,
    });
    await router.replace({
      path: route.path,
      query: Object.keys(query).length ? query : undefined,
    });
    return;
  }

  const reward = rewardsStore.allRewards.find((item) => item.id === rewardId);

  if (!reward) {
    commonStore.setAlert({
      open: true,
      type: 'error',
      title: t('actionDeepLink.notFound.title'),
      description: t('actionDeepLink.notFound.reward'),
      duration: 5000,
    });
    await router.replace({
      path: route.path,
      query: Object.keys(query).length ? query : undefined,
    });
    return;
  }

  await nextTick();
  onRewardClick(reward);
  await router.replace({
    path: route.path,
    query: Object.keys(query).length ? query : undefined,
  });
});

// Methods
const onCloseRewardDetails = () => {
  rewardsStore.setRewardDetails(false);

  rewardDetails.value = null;
};

const onRewardClick = (reward: UserDashboardGetRewardsResponseType[number]) => {
  if (userStore.user) {
    if (!mq.lgPlus) {
      rewardsStore.setRewardDetails(true);
      rewardDetails.value = reward;
    } else if (mq.lgPlus) {
      commonStore.setManageRewardsModal({
        description: reward.description,
        open: true,
        item: reward,
        role: 'reward',
        title: reward.name,
      });
    }
  }
};
</script>

<template>
  <div
    class="block h-full flex-1 px-4 pb-8 md:px-8"
    :class="[
      rewardsStore.allRewards?.length
        ? 'content-start lg:flex lg:flex-wrap'
        : 'flex flex-1',
    ]"
  >
    <template v-if="initialized && rewardsStore.allRewards?.length">
      <template v-if="!rewardDetails">
        <BaseCard
          v-for="(reward, rewardIndex) in rewardsStore.allRewards
            .slice()
            .sort((a, b) => a.points - b.points)"
          :key="rewardIndex"
          class="mb-2 lg:mb-6 lg:mr-6"
          :title="reward.name"
          :img-url="reward.pictureUrl ? reward.pictureUrl : '/img/reward.png'"
          :badge-status="{ points: reward.points }"
          contain-img
          @click="onRewardClick(reward)"
        />
      </template>

      <BaseRewardDetails
        v-else
        :reward="{ ...rewardDetails, status: undefined }"
        @close="onCloseRewardDetails"
      />
    </template>

    <BaseEmptyState
      v-else
      class="flex flex-1 flex-col items-center justify-center"
      :text="$t('rewardsView.empty')"
      type="empty-list"
    />
  </div>
</template>
